import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import SEO from '../../components/SEO';
import Breadcrumb from '../../components/Breadcrumb';
import { buildBreadcrumbJsonLd } from '../../lib/seoSchema';
import { pageSeoMeta } from '@shared/pageSeoMeta';

export default function TermsOfServicePage() {
  const { isDark } = useTheme();

  return (
    <div className={`min-h-screen pb-20 transition-colors duration-300 ${isDark ? 'bg-gray-950 text-gray-300' : 'bg-white text-gray-700'}`}>
      <SEO
        {...pageSeoMeta.policyTerms}
        jsonLd={buildBreadcrumbJsonLd([
          { name: 'Home', url: '/' },
          { name: 'Terms of Service', url: '/policies/terms' },
        ])}
      />

      <Breadcrumb items={[
        { name: 'Home', href: '/' },
        { name: 'Terms of Service' },
      ]} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
        <Link to="/" className="inline-flex items-center gap-2 text-emerald-500 hover:text-emerald-400 mb-8 text-sm font-medium">
          <ArrowLeft size={16} /> Back to Home
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h1 className={`text-3xl sm:text-4xl font-extrabold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Terms of Service</h1>
          <p className={`text-sm mb-10 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Last updated: March 2026</p>

          <div className="space-y-8 leading-relaxed">
            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>1. Acceptance of Terms</h2>
              <p>By accessing and using the ZeroSmoke website (zerosmoke.in), you agree to be bound by these Terms of Service. If you do not agree with any part of these terms, please discontinue use of our website.</p>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>2. Age Restriction</h2>
              <p>You must be <strong>18 years or older</strong> to purchase products from ZeroSmoke. By placing an order, you confirm that you meet this age requirement. We reserve the right to cancel orders if age verification fails.</p>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>3. Products</h2>
              <ul className="list-disc list-inside space-y-2">
                <li>All products sold on ZeroSmoke are nicotine pouches — <strong>tobacco-free and smoke-free</strong>.</li>
                <li>Product images are for illustrative purposes. Actual packaging may vary slightly.</li>
                <li>We make every effort to ensure product descriptions and prices are accurate, but errors may occur.</li>
              </ul>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>4. Pricing & Payment</h2>
              <ul className="list-disc list-inside space-y-2">
                <li>All prices are listed in <strong>Indian Rupees (₹)</strong> and include applicable taxes.</li>
                <li>We accept UPI, credit/debit cards, net banking, and select wallets.</li>
                <li>Payment must be completed at the time of order placement.</li>
              </ul>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>5. Order Cancellation</h2>
              <p>Orders can be cancelled within <strong>12 hours</strong> of placement, provided they haven't been shipped. Contact us at <a href="mailto:zerosmoke2026@gmail.com" className="text-emerald-500 hover:underline">zerosmoke2026@gmail.com</a> to request cancellation.</p>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>6. Intellectual Property</h2>
              <p>All content on this website — including logos, images, text, and design — is the property of ZeroSmoke and may not be reproduced, distributed, or used without prior written permission.</p>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>7. Limitation of Liability</h2>
              <p>ZeroSmoke is not liable for any damages arising from the use or inability to use our products or website. Use nicotine products responsibly and at your own risk.</p>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>8. Changes to Terms</h2>
              <p>We reserve the right to update these terms at any time. Changes will be posted on this page with an updated date. Continued use of the site constitutes acceptance of the revised terms.</p>
            </section>

            <section>
              <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Contact</h2>
              <p>Questions about these terms? Email us at <a href="mailto:zerosmoke2026@gmail.com" className="text-emerald-500 hover:underline">zerosmoke2026@gmail.com</a>.</p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
